import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  AlertCircle,
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Package,
  ShoppingCart,
  Building2,
  Database,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";

interface SyncRecord {
  id: string;
  timestamp: Date;
  entityType: "Stock Items" | "Stock Movements" | "Purchase Orders" | "Suppliers" | "Warehouses";
  operation: "Create" | "Update" | "Delete";
  status: "success" | "failed" | "pending" | "partial";
  recordsProcessed: number;
  recordsTotal: number;
  source: "Access Financials" | "Access Dimensions";
  duration: number; // in seconds
  errorMessage?: string;
  reference?: string;
}

type SortColumn = "timestamp" | "entityType" | "status" | "recordsProcessed" | "duration";
type SortDirection = "asc" | "desc";

// Generate mock sync records
const generateMockSyncRecords = (): SyncRecord[] => {
  const records: SyncRecord[] = [];
  const now = new Date();
  
  const entityTypes: SyncRecord["entityType"][] = [
    "Stock Items",
    "Stock Movements",
    "Purchase Orders",
    "Suppliers",
    "Warehouses",
  ];
  
  const operations: SyncRecord["operation"][] = ["Create", "Update", "Delete"];
  const statuses: SyncRecord["status"][] = ["success", "success", "success", "success", "failed", "partial"];
  const sources: SyncRecord["source"][] = ["Access Financials", "Access Financials", "Access Financials"];
  
  // Generate 50 sync records over the past 7 days
  for (let i = 0; i < 50; i++) {
    const daysAgo = Math.floor(Math.random() * 7);
    const hoursAgo = Math.floor(Math.random() * 24);
    const minutesAgo = Math.floor(Math.random() * 60);
    
    const timestamp = new Date(now);
    timestamp.setDate(timestamp.getDate() - daysAgo);
    timestamp.setHours(timestamp.getHours() - hoursAgo);
    timestamp.setMinutes(timestamp.getMinutes() - minutesAgo);
    
    const entityType = entityTypes[Math.floor(Math.random() * entityTypes.length)];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const source = sources[Math.floor(Math.random() * sources.length)];
    
    let recordsTotal = 0;
    let recordsProcessed = 0;
    
    switch (entityType) {
      case "Stock Items":
        recordsTotal = Math.floor(Math.random() * 500) + 100;
        break;
      case "Stock Movements":
        recordsTotal = Math.floor(Math.random() * 200) + 50;
        break;
      case "Purchase Orders":
        recordsTotal = Math.floor(Math.random() * 50) + 10;
        break;
      case "Suppliers":
        recordsTotal = Math.floor(Math.random() * 30) + 5;
        break;
      case "Warehouses":
        recordsTotal = Math.floor(Math.random() * 10) + 1;
        break;
    }
    
    if (status === "success") {
      recordsProcessed = recordsTotal;
    } else if (status === "partial") {
      recordsProcessed = Math.floor(recordsTotal * (0.5 + Math.random() * 0.4));
    } else if (status === "failed") {
      recordsProcessed = 0;
    } else {
      recordsProcessed = Math.floor(recordsTotal * Math.random());
    }
    
    const duration = Math.floor(Math.random() * 120) + 5;
    
    records.push({
      id: `SYNC-${String(i + 1).padStart(4, "0")}`,
      timestamp,
      entityType,
      operation,
      status,
      recordsProcessed,
      recordsTotal,
      source,
      duration,
      errorMessage: status === "failed" ? "Connection timeout to ERP system" : 
                    status === "partial" ? "Some records failed validation" : undefined,
      reference: `REF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
    });
  }
  
  return records.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

export function SyncStatusPage() {
  const [syncRecords] = useState<SyncRecord[]>(generateMockSyncRecords());
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [sortColumn, setSortColumn] = useState<SortColumn>("timestamp");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Filter and sort records
  const filteredRecords = useMemo(() => {
    let filtered = syncRecords;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (record) =>
          record.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.entityType.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.reference?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((record) => record.status === statusFilter);
    }

    // Entity filter
    if (entityFilter !== "all") {
      filtered = filtered.filter((record) => record.entityType === entityFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortColumn];
      let bValue: any = b[sortColumn];

      if (sortColumn === "timestamp") {
        aValue = a.timestamp.getTime();
        bValue = b.timestamp.getTime();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [syncRecords, searchTerm, statusFilter, entityFilter, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = filteredRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const startItem = filteredRecords.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredRecords.length);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Summary statistics
  const summaryStats = useMemo(() => {
    const last24Hours = syncRecords.filter(
      (r) => new Date().getTime() - r.timestamp.getTime() < 24 * 60 * 60 * 1000
    );
    
    return {
      totalSyncs: last24Hours.length,
      successful: last24Hours.filter((r) => r.status === "success").length,
      failed: last24Hours.filter((r) => r.status === "failed").length,
      partial: last24Hours.filter((r) => r.status === "partial").length,
      totalRecordsProcessed: last24Hours.reduce((sum, r) => sum + r.recordsProcessed, 0),
    };
  }, [syncRecords]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const handleSyncNow = () => {
    toast.success("Manual sync initiated successfully");
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getStatusBadge = (status: SyncRecord["status"]) => {
    switch (status) {
      case "success":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Success
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case "partial":
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Partial
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const getEntityIcon = (entityType: SyncRecord["entityType"]) => {
    switch (entityType) {
      case "Stock Items":
        return <Package className="h-4 w-4" />;
      case "Stock Movements":
        return <TrendingUp className="h-4 w-4" />;
      case "Purchase Orders":
        return <ShoppingCart className="h-4 w-4" />;
      case "Suppliers":
        return <Building2 className="h-4 w-4" />;
      case "Warehouses":
        return <Database className="h-4 w-4" />;
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Sync Status</h1>
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            🟡 Demo Mode
          </Badge>
        </div>
        <p className="text-muted-foreground mt-2">
          Monitor synchronisation status between Stock Evo and Access Financials.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Syncs (24h)</CardTitle>
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryStats.totalSyncs}</div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summaryStats.successful}</div>
            <p className="text-xs text-muted-foreground">
              {summaryStats.totalSyncs > 0
                ? `${Math.round((summaryStats.successful / summaryStats.totalSyncs) * 100)}% success rate`
                : "No syncs"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{summaryStats.failed}</div>
            <p className="text-xs text-muted-foreground">Requires attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Partial</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{summaryStats.partial}</div>
            <p className="text-xs text-muted-foreground">Partially completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Records Synced</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryStats.totalRecordsProcessed.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>
      </div>

      {/* Alert for failed syncs */}
      {summaryStats.failed > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {summaryStats.failed} sync operation{summaryStats.failed > 1 ? "s" : ""} failed in the last 24 hours. 
            Check the logs below for details.
          </AlertDescription>
        </Alert>
      )}

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Sync History</CardTitle>
              <CardDescription>View all synchronisation operations and their status</CardDescription>
            </div>
            <Button onClick={handleSyncNow}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Now
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search and Filters */}
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by ID, entity type, or reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Entity Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="Stock Items">Stock Items</SelectItem>
                  <SelectItem value="Stock Movements">Stock Movements</SelectItem>
                  <SelectItem value="Purchase Orders">Purchase Orders</SelectItem>
                  <SelectItem value="Suppliers">Suppliers</SelectItem>
                  <SelectItem value="Warehouses">Warehouses</SelectItem>
                </SelectContent>
              </Select>
            </div>



            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort("timestamp")}
                    >
                      Timestamp
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort("entityType")}
                    >
                      Entity Type
                    </TableHead>
                    <TableHead>Operation</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort("status")}
                    >
                      Status
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort("recordsProcessed")}
                    >
                      Records
                    </TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort("duration")}
                    >
                      Duration
                    </TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No sync records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">
                          {formatTimestamp(record.timestamp)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getEntityIcon(record.entityType)}
                            <span>{record.entityType}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{record.operation}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(record.status)}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="text-sm">
                              {record.recordsProcessed} / {record.recordsTotal}
                            </div>
                            <Progress 
                              value={(record.recordsProcessed / record.recordsTotal) * 100} 
                              className="h-1"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{record.source}</TableCell>
                        <TableCell className="text-sm">{record.duration}s</TableCell>
                        <TableCell className="font-mono text-xs">{record.reference}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Items per page:</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground ml-4">
                  Showing {startItem}-{endItem} of {filteredRecords.length} records
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* First Page Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>

                {/* Previous Page Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {/* Page Number Buttons */}
                {getPageNumbers().map((page, index) => (
                  <Button
                    key={index}
                    variant={page === currentPage ? "default" : "outline"}
                    size="icon"
                    onClick={() => typeof page === "number" && setCurrentPage(page)}
                    disabled={typeof page === "string"}
                    className={typeof page === "string" ? "cursor-default" : ""}
                  >
                    {page}
                  </Button>
                ))}

                {/* Next Page Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>

                {/* Last Page Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
